<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Tendencias vs Periodo Anterior',
  },
});

const currentMetrics = computed(
  () => props.data?.current_period?.metrics || {}
);
const previousMetrics = computed(
  () => props.data?.previous_period?.metrics || {}
);
const changes = computed(() => props.data?.changes || {});

const metrics = [
  { key: 'conversations', label: 'Conversas', icon: 'i-lucide-message-square' },
  { key: 'resolved', label: 'Resolvidas', icon: 'i-lucide-check-circle' },
  {
    key: 'resolution_rate',
    label: 'Taxa Resolucao',
    icon: 'i-lucide-percent',
    unit: '%',
  },
  { key: 'csat_average', label: 'CSAT Medio', icon: 'i-lucide-star' },
  {
    key: 'sla_compliance',
    label: 'SLA',
    icon: 'i-lucide-shield-check',
    unit: '%',
  },
  {
    key: 'avg_response_minutes',
    label: '1a Resposta',
    icon: 'i-lucide-timer',
    unit: ' min',
    inverse: true,
  },
];

function getChangeColor(direction, inverse = false) {
  if (direction === 'neutral') return 'text-n-slate-11';
  if (inverse) {
    return direction === 'up' ? 'text-n-ruby-11' : 'text-n-teal-11';
  }
  return direction === 'up' ? 'text-n-teal-11' : 'text-n-ruby-11';
}

function getChangeIcon(direction) {
  if (direction === 'up') return 'i-lucide-trending-up';
  if (direction === 'down') return 'i-lucide-trending-down';
  return 'i-lucide-minus';
}

function formatValue(value, unit = '') {
  if (value === null || value === undefined) return '-';
  return `${value}${unit}`;
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-trending-up text-lg text-n-teal-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <div v-else class="flex flex-col gap-3">
      <div
        v-for="metric in metrics"
        :key="metric.key"
        class="flex items-center justify-between p-3 rounded-lg bg-n-alpha-1"
      >
        <div class="flex items-center gap-2">
          <span :class="metric.icon" class="text-n-slate-10" />
          <span class="text-sm text-n-slate-12">{{ metric.label }}</span>
        </div>
        <div class="flex items-center gap-3">
          <div class="text-right">
            <div class="text-sm font-bold text-n-slate-12">
              {{ formatValue(currentMetrics[metric.key], metric.unit) }}
            </div>
            <div class="text-xs text-n-slate-10">
              Antes: {{ formatValue(previousMetrics[metric.key], metric.unit) }}
            </div>
          </div>
          <div
            v-if="changes[metric.key]"
            class="flex items-center gap-1 px-2 py-1 rounded"
            :class="{
              'bg-n-teal-3':
                getChangeColor(
                  changes[metric.key].direction,
                  metric.inverse
                ) === 'text-n-teal-11',
              'bg-n-ruby-3':
                getChangeColor(
                  changes[metric.key].direction,
                  metric.inverse
                ) === 'text-n-ruby-11',
              'bg-n-alpha-2': changes[metric.key].direction === 'neutral',
            }"
          >
            <span
              :class="[
                getChangeIcon(changes[metric.key].direction),
                getChangeColor(changes[metric.key].direction, metric.inverse),
              ]"
            />
            <span
              class="text-xs font-medium"
              :class="
                getChangeColor(changes[metric.key].direction, metric.inverse)
              "
            >
              {{ changes[metric.key].percentage > 0 ? '+' : ''
              }}{{ changes[metric.key].percentage }}%
            </span>
          </div>
        </div>
      </div>

      <!-- Period Info -->
      <div
        class="flex items-center justify-between text-xs text-n-slate-11 pt-2 border-t border-n-weak"
      >
        <span>Periodo atual: {{ data.current_period?.start?.split('T')[0] }} a
          {{ data.current_period?.end?.split('T')[0] }}</span>
        <span>vs {{ data.previous_period?.start?.split('T')[0] }} a
          {{ data.previous_period?.end?.split('T')[0] }}</span>
      </div>
    </div>
  </div>
</template>
